/**
 * The suggestion lists behind the free-text clinical fields.
 *
 * The legacy app's "masters" — present complaints, notifiable diseases, eye and dental
 * diagnoses, treatments — are lists that grow themselves: anything a
 * clinician types that is not on the list is added. Ours work the same way
 * and stay what they are: suggestions. A free-text field accepts whatever is
 * typed; the list only shortens the typing and keeps spellings together. It
 * is not a coded answer set and nothing is flagged or counted from it.
 *
 * Dependency-free and shared with the SaaS through `@dru/clinical-math`,
 * so the station offers the list the API learns from.
 */

export const SUGGESTION_LISTS = {
  COMPLAINT: 'Present complaints',
  NOTIFIABLE_DISEASE: 'Notifiable diseases',
  VISUAL_COMPLAINT: 'Visual complaints',
  ORAL_COMPLAINT: 'Oral complaints',
  EYE_DIAGNOSIS: 'Eye diagnoses',
  DENTAL_DIAGNOSIS: 'Dental diagnoses',
  PROVISIONAL_DIAGNOSIS: 'Provisional diagnoses',
  DENTAL_TREATMENT: 'Dental treatments',
  PHYSICIAN_TREATMENT: 'Physician treatments',
} as const;
export type SuggestionList = keyof typeof SUGGESTION_LISTS;

/** Which free-text test reads which list. */
export const SUGGESTION_LIST_BY_TEST: Readonly<Record<string, SuggestionList>> = {
  'HIST-COMPLAINTS': 'COMPLAINT',
  'HIST-NOTIFIABLE': 'NOTIFIABLE_DISEASE',
  'VIS-COMPLAINTS': 'VISUAL_COMPLAINT',
  'DEN-COMPLAINTS': 'ORAL_COMPLAINT',
  'VIS-DIAGNOSIS': 'EYE_DIAGNOSIS',
  'DEN-DIAGNOSIS': 'DENTAL_DIAGNOSIS',
  'PHY-DIAGNOSIS': 'PROVISIONAL_DIAGNOSIS',
  'DEN-TREATMENT': 'DENTAL_TREATMENT',
  'PHY-TREATMENT': 'PHYSICIAN_TREATMENT',
};

export function suggestionListFor(testCode: string): SuggestionList | null {
  return SUGGESTION_LIST_BY_TEST[testCode] ?? null;
}

/** "Backache, fatigue; fever" → ["Backache", "fatigue", "fever"]: what a typed field adds to its list. */
export function splitTyped(value: string | null | undefined): string[] {
  if (!value) return [];
  return value.split(/[;,\n]+/).map((s) => s.trim()).filter((s) => s.length >= 2 && s.length <= 80);
}

/** The key two spellings of one thing share: case, spacing and trailing punctuation folded. */
export function normaliseSuggestion(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ').replace(/[.。]+$/, '');
}
